import React, { useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tooltip,
  Grid,
} from '@mui/material';
import { WidgetToolboxProps } from '../types';
import { hasKey, useI18n } from '../../i18n';

export const WidgetToolbox: React.FC<WidgetToolboxProps> = ({
  itemTypes,
  allowedTypes,
  onWidgetAdd,
  compact = false,
}) => {
  const { t } = useI18n();
  const types = (allowedTypes && allowedTypes.length
    ? allowedTypes.filter((type) => itemTypes[type])
    : Object.keys(itemTypes));
  const handleWidgetClick = useCallback((widgetType: string) => {
    onWidgetAdd(widgetType);
  }, [onWidgetAdd]);

  return (
    <Paper
      elevation={0}
      variant="outlined"
      sx={{
        p: compact ? 1.25 : 2,
        overflow: 'visible',
        flexShrink: 0,
      }}
    >
      <Typography
        variant={compact ? 'caption' : 'h6'}
        color={compact ? 'text.secondary' : undefined}
        sx={{ display: 'block', mb: compact ? 1 : 1.5 }}
      >
        {t('layout.toolbox')}
      </Typography>

      {types.length === 0 ? (
        <Typography variant="body2" color="text.secondary">{t('layout.schemaMissing')}</Typography>
      ) : (
        <Grid container spacing={1}>
          {types.map((type) => {
            const spec = itemTypes[type];
            const label = hasKey(`layout.widget.${type}`) ? t(`layout.widget.${type}`) : (spec.title || type);
            const hint = hasKey(`layout.widgetHint.${type}`) ? t(`layout.widgetHint.${type}`) : label;
            return (
              <Grid item xs={4} key={type}>
                <Tooltip title={hint}>
                  <Paper
                    elevation={0}
                    variant="outlined"
                    sx={{
                      py: compact ? 1.25 : 1.5,
                      px: 0.75,
                      minHeight: compact ? 64 : 72,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'background-color 0.15s',
                      '&:hover': {
                        bgcolor: 'action.hover'
                      }
                    }}
                    onClick={() => handleWidgetClick(type)}
                  >
                    <Box sx={{ fontSize: compact ? '1.35rem' : '1.5rem', lineHeight: 1, mb: 0.5 }}>
                      {spec.icon || '▫️'}
                    </Box>
                    <Typography variant="caption" textAlign="center" sx={{ lineHeight: 1.2 }}>
                      {label}
                    </Typography>
                  </Paper>
                </Tooltip>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Paper>
  );
};
